/**
 * Turns level primitives into meshes.
 *
 * The *same* array that the collision math reads drives every mesh here, so the
 * course you see and the course you bounce off cannot drift apart. Three unit
 * geometries are shared across the whole level and scaled per primitive; one
 * flat-shaded Lambert material is cached per distinct colour, which — because
 * `level.ts` hands out one shared `Material` object per tag — comes out at about
 * nine materials for ~150 meshes.
 *
 * Almost all of it is frozen: `matrixAutoUpdate` goes off once the world
 * matrices are baked and nothing ever touches them again. The exception is the
 * primitives carrying a `mover`, which come back as {@link DynamicMeshEntry}
 * records for {@link updateMoverMeshes} to re-pose once per rendered frame —
 * from the *same* `applyMover` the physics evaluates, at a clock deliberately
 * lagged to match the body's interpolation, so a rider never floats a substep
 * away from the platform under it.
 */

import {
  AdditiveBlending,
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Quaternion,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import { applyMover, clonePrimitive, type BlinkState, type MoverSpec } from '@shared/movers';
import type { LevelData, Primitive } from '@shared/primitives';
import { blinkStateAt, type MoverSystem } from '../physics/moverSystem';

// --- shared geometry (module-level: one copy for the whole app) -------------

/** Unit cube, centred at the origin. Scaled by `half * 2`. */
const UNIT_BOX: BufferGeometry = new BoxGeometry(1, 1, 1);
/** Unit sphere. Scaled by the radius. */
const UNIT_SPHERE: BufferGeometry = new SphereGeometry(1, 16, 12);
/** Unit cylinder: radius 1, height 1, along +Y, capped. Scaled to (r, len, r). */
const UNIT_CYLINDER: BufferGeometry = new CylinderGeometry(1, 1, 1, 16, 1);

const UP = new Vector3(0, 1, 0);
const _a = new Vector3();
const _b = new Vector3();
const _dir = new Vector3();
const _quat = new Quaternion();

/**
 * One material per colour, shared across levels and rebuilds. Never disposed —
 * the level is static and this cache is bounded by the palette size.
 */
const materials = new Map<number, MeshLambertMaterial>();

function materialFor(color: number): MeshLambertMaterial {
  let mat = materials.get(color);
  if (mat === undefined) {
    mat = new MeshLambertMaterial({ color, flatShading: true });
    materials.set(color, mat);
  }
  return mat;
}

function shadowed(mesh: Mesh): Mesh {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Orient a segment primitive: rotate +Y onto `a → b`, scale to (r, length, r)
 * and sit at the midpoint. Returns the segment length.
 */
function placeSegment(mesh: Mesh, a: readonly number[], b: readonly number[], radius: number): number {
  _a.set(a[0] as number, a[1] as number, a[2] as number);
  _b.set(b[0] as number, b[1] as number, b[2] as number);
  _dir.copy(_b).sub(_a);
  const len = _dir.length();
  mesh.position.copy(_a).addScaledVector(_dir, 0.5);
  if (len > 1e-6) {
    _quat.setFromUnitVectors(UP, _dir.multiplyScalar(1 / len));
    mesh.quaternion.copy(_quat);
  }
  mesh.scale.set(radius, Math.max(len, 1e-6), radius);
  return len;
}

/**
 * Add the mesh (or, for a capsule, the three meshes) one primitive draws as.
 *
 * `out`, when supplied, collects them in the order {@link poseMeshes} expects:
 * the body first, then the `a` and `b` end caps.
 */
function addPrimitive(group: Group, prim: Primitive, mat: MeshLambertMaterial, out?: Mesh[]): void {
  switch (prim.kind) {
    case 'box': {
      const mesh = new Mesh(UNIT_BOX, mat);
      mesh.position.set(prim.center[0], prim.center[1], prim.center[2]);
      mesh.quaternion.set(prim.quat[0], prim.quat[1], prim.quat[2], prim.quat[3]);
      mesh.scale.set(prim.half[0] * 2, prim.half[1] * 2, prim.half[2] * 2);
      group.add(shadowed(mesh));
      out?.push(mesh);
      break;
    }
    case 'sphere': {
      const mesh = new Mesh(UNIT_SPHERE, mat);
      mesh.position.set(prim.center[0], prim.center[1], prim.center[2]);
      mesh.scale.setScalar(prim.radius);
      group.add(shadowed(mesh));
      out?.push(mesh);
      break;
    }
    case 'cylinder': {
      const mesh = new Mesh(UNIT_CYLINDER, mat);
      placeSegment(mesh, prim.a, prim.b, prim.radius);
      group.add(shadowed(mesh));
      out?.push(mesh);
      break;
    }
    case 'capsule': {
      // Body plus two hemispherical ends, from the same shared geometries.
      const body = new Mesh(UNIT_CYLINDER, mat);
      placeSegment(body, prim.a, prim.b, prim.radius);
      group.add(shadowed(body));
      out?.push(body);
      for (const end of [prim.a, prim.b]) {
        const cap = new Mesh(UNIT_SPHERE, mat);
        cap.position.set(end[0], end[1], end[2]);
        cap.scale.setScalar(prim.radius);
        group.add(shadowed(cap));
        out?.push(cap);
      }
      break;
    }
  }
}

// --- movers -----------------------------------------------------------------

/**
 * One moving primitive and the meshes it draws as.
 *
 * The meshes sit in the level group like everything else — a mover is not a
 * separate scene branch — but their transforms are re-derived from the posed
 * primitive every frame rather than baked once. Deriving rather than
 * accumulating is what keeps a capsule's caps welded to its body and a
 * cylinder's orientation honest: both come out of `a`/`b`, and rotating a
 * cached matrix would drift away from the collision shape within a minute.
 */
export interface DynamicMeshEntry {
  /** The live level primitive — the same object the physics collides against. */
  readonly prim: Primitive;
  /** Rest pose, captured before the mover system ever ticks. */
  readonly base: Primitive;
  /** Scratch target `applyMover` writes this frame's pose into. */
  readonly pose: Primitive;
  readonly spec: MoverSpec;
  readonly meshes: Mesh[];
  /**
   * A private clone, for `blink` only: the fade before a platform vanishes
   * writes `opacity`, and the shared cache is shared with every other primitive
   * of that colour.
   */
  readonly material: MeshLambertMaterial | null;
}

/**
 * Build every mesh for a level, in one group ready to `scene.add`.
 *
 * Everything static is frozen: `matrixAutoUpdate` goes off once the world
 * matrices are baked. The returned `dynamic` list is the rest, and it must be
 * handed to {@link updateMoverMeshes} once per frame or the movers will draw at
 * their rest pose while the physics moves them.
 *
 * Call this **before** the first {@link MoverSystem.tick}: the rest poses are
 * read straight off `level.primitives`, which the tick overwrites.
 */
export function buildLevelMeshes(level: LevelData): { group: Group; dynamic: DynamicMeshEntry[] } {
  const group = new Group();
  group.name = 'level';
  const dynamic: DynamicMeshEntry[] = [];

  for (const prim of level.primitives) {
    const spec = prim.mover;
    if (spec === undefined) {
      addPrimitive(group, prim, materialFor(prim.material.color));
      continue;
    }
    let material: MeshLambertMaterial | null = null;
    if (spec.type === 'blink') {
      material = materialFor(prim.material.color).clone();
      material.transparent = true;
    }
    const meshes: Mesh[] = [];
    addPrimitive(group, prim, material ?? materialFor(prim.material.color), meshes);
    dynamic.push({
      prim,
      base: clonePrimitive(prim),
      pose: clonePrimitive(prim),
      spec,
      meshes,
      material,
    });
  }

  group.updateMatrixWorld(true);
  group.matrixAutoUpdate = false;
  // The movers keep `matrixAutoUpdate` off too — `updateMoverMeshes` calls
  // `updateMatrix` itself, which is the same work without the per-frame check.
  for (const child of group.children) child.matrixAutoUpdate = false;
  return { group, dynamic };
}

/** Seconds of flicker before a blink platform goes. Long enough to be a warning. */
const BLINK_WARN_S = 0.9;
/** Radians per second the warning flicker oscillates at. */
const BLINK_FLICKER_RATE = 30;

const _blink: BlinkState = { on: true, remaining: Infinity };

/** Write `entry.pose`'s transform into its meshes. Allocation-free. */
function poseMeshes(entry: DynamicMeshEntry): void {
  const p = entry.pose;
  const meshes = entry.meshes;
  switch (p.kind) {
    case 'box': {
      const mesh = meshes[0]!;
      mesh.position.set(p.center[0], p.center[1], p.center[2]);
      mesh.quaternion.set(p.quat[0], p.quat[1], p.quat[2], p.quat[3]);
      break;
    }
    case 'sphere':
      meshes[0]!.position.set(p.center[0], p.center[1], p.center[2]);
      break;
    case 'cylinder':
      placeSegment(meshes[0]!, p.a, p.b, p.radius);
      break;
    case 'capsule':
      placeSegment(meshes[0]!, p.a, p.b, p.radius);
      meshes[1]!.position.set(p.a[0], p.a[1], p.a[2]);
      meshes[2]!.position.set(p.b[0], p.b[1], p.b[2]);
      break;
  }
  for (let i = 0; i < meshes.length; i++) meshes[i]!.updateMatrix();
}

/**
 * Re-pose every mover for one rendered frame. Call before `renderer.render`.
 *
 * @param alpha the loop's interpolation factor, the same one `getPose` takes —
 *   {@link MoverSystem.renderTime} turns it into the clock the body is being
 *   drawn at, so the platform and its rider move as one.
 */
export function updateMoverMeshes(
  dynamic: DynamicMeshEntry[],
  movers: MoverSystem,
  alpha: number,
): void {
  const t = movers.renderTime(alpha);
  for (let i = 0; i < dynamic.length; i++) {
    const entry = dynamic[i]!;
    if (entry.spec.type === 'blink') {
      // A blink platform never moves; all that changes is whether it is there,
      // and the flicker that warns it is about to stop being there. Landing on
      // one that vanishes is the mechanic — landing on one with no warning is
      // just a trap.
      blinkStateAt(entry.spec, t, _blink);
      for (let k = 0; k < entry.meshes.length; k++) entry.meshes[k]!.visible = _blink.on;
      if (entry.material !== null) {
        entry.material.opacity =
          _blink.remaining < BLINK_WARN_S
            ? 0.35 + 0.4 * Math.abs(Math.sin(BLINK_FLICKER_RATE * t))
            : 1;
      }
      continue;
    }
    applyMover(entry.base, entry.spec, t, entry.pose);
    poseMeshes(entry);
  }
}

// --- personal-best marker ---------------------------------------------------

/**
 * Radius of the PB ring: wide enough to read as a horizon line at any altitude.
 *
 * It has to stay outside *everything* the level builds, or it stops being a
 * horizon and becomes a hoop threaded through the mountain. The furthest the
 * course itself may reach is `R_HARD_MAX` (34 m) and the furthest a landmark may
 * is `LANDMARK_R_MAX` (40 m) — so 40 was exactly on the line and is now clear of
 * it by fifteen metres, which is also enough that the ring reads as *around* the
 * mountain rather than as a collar sitting on it.
 */
const PB_RING_RADIUS = 55;

/**
 * A translucent, additively-blended ring to park at the player's personal-best
 * altitude — visible from anywhere on the course without ever occluding it.
 *
 * The caller owns placement: set `mesh.position.y` to the PB height each time it
 * changes. Fog is disabled so it stays legible across the shaft.
 */
export function buildPbRing(): Mesh {
  const geo = new TorusGeometry(PB_RING_RADIUS, 0.22, 6, 96);
  geo.rotateX(-Math.PI / 2); // lie flat in the XZ plane
  const mat = new MeshBasicMaterial({
    color: 0xffd27a,
    transparent: true,
    opacity: 0.32,
    depthWrite: false,
    blending: AdditiveBlending,
    fog: false,
  });
  const mesh = new Mesh(geo, mat);
  mesh.name = 'pbRing';
  mesh.renderOrder = 2;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

// --- checkpoint marker ------------------------------------------------------

/** How far the beacon beam rises above its pad, metres. */
const BEACON_HEIGHT = 14;

/**
 * A glowing beam standing on the checkpoint you would come back to. Same
 * additive, fog-free family as the PB ring, so both markers read as UI rather
 * than as something you could land on.
 *
 * Open-ended and double-sided on purpose: you climb *through* this thing, and a
 * capped cylinder would slap a lid across the screen on the way past. The
 * geometry is baked with its base at the mesh origin, so the caller places it
 * with a plain `position.copy(padTop)`.
 */
export function buildCheckpointBeacon(): Mesh {
  const geo = new CylinderGeometry(0.45, 0.45, 1, 12, 1, true);
  geo.translate(0, 0.5, 0); // origin at the base, not the centre
  geo.scale(1, BEACON_HEIGHT, 1);
  const mat = new MeshBasicMaterial({
    color: 0x3fd0b4,
    transparent: true,
    opacity: 0.25,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
    fog: false,
  });
  const mesh = new Mesh(geo, mat);
  mesh.name = 'checkpointBeacon';
  mesh.renderOrder = 2;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}
